'use client'

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { PopupModal } from 'react-calendly'
import { FiMessageCircle } from 'react-icons/fi'

type CalendlyButtonProps = {
  isCollapsed: boolean
  calendlyUrl?: string
}

// Variants to handle animation of the icon separately from the parent container
const buttonVariants = {
  hover: { scale: 1.02 },
  tap: { scale: 0.98 },
}

const iconVariants = {
  hover: {
    x: [0, -1, 1, -1, 1, 0],
    transition: { duration: 0.4 },
  },
}

export function CalendlyButton({
  isCollapsed,
  calendlyUrl = 'https://calendly.com/spritesai/sprites-support',
}: CalendlyButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const layoutContainer = document.querySelector('[data-id="layout-container"]') as HTMLElement
      const nextRoot = document.getElementById('__next')
      setRootElement(layoutContainer || nextRoot || document.body)
    }
  }, [])

  const handleClick = () => {
    setIsOpen(true)
  }

  if (isCollapsed) {
    return (
      <>
        <motion.button
          onClick={handleClick}
          className="hover:bg-ui-hover flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          title="Have Questions?"
        >
          <motion.div whileHover={{ x: [0, -2, 2, -2, 2, 0], transition: { duration: 0.4 } }}>
            <FiMessageCircle className="text-ui-secondary h-5 w-5" />
          </motion.div>
        </motion.button>

        {rootElement && (
          <PopupModal
            url={calendlyUrl}
            onModalClose={() => setIsOpen(false)}
            open={isOpen}
            rootElement={rootElement}
          />
        )}
      </>
    )
  }

  return (
    <>
      <motion.button
        onClick={handleClick}
        className="text-ui-text hover:bg-ui-hover flex w-full items-center gap-3 rounded-lg p-2 transition-colors"
        variants={buttonVariants}
        whileHover="hover"
        whileTap="tap"
      >
        <motion.div variants={iconVariants}>
          <FiMessageCircle className="text-ui-secondary h-5 w-5 flex-shrink-0" />
        </motion.div>
        <span className="text-sm font-medium">Have Questions?</span>
      </motion.button>

      {rootElement && (
        <PopupModal
          url={calendlyUrl}
          onModalClose={() => setIsOpen(false)}
          open={isOpen}
          rootElement={rootElement}
        />
      )}
    </>
  )
}
